import { useCallback, useEffect, useMemo, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useDebouncedValue } from '../../../shared/ui/hooks-ui/useDebouncedValue';
import { effectiveProductSearchQuery, PRODUCT_SEARCH_DEBOUNCE_MS } from '../../../shared/product-search/productSearchUx';
import { categoryAppliesToDraftItemType, normalizeProductCategoryId } from '../../../catalog/domain-api'
import type { CategoryRecord } from '../../../catalog/domain-api'
import type { PosCatalogItemTypeFilter } from '../../domain/posTypes';
import { listDistinctProductBrandsLocal } from '../../../catalog/infra-api'
import { listActiveSuppliersLocal } from '../../../catalog/infra-api';
import { POS_GRID_PHYSICAL_STOCK_ONLY_STORAGE_KEY } from '../../application/posLayout.constants';
import { buildCategoryOptions } from '../../../catalog/domain-api';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { ticketColors } from '../../../shared/theme/ticketColors';

const EMPTY_CATEGORY_FALLBACK: CategoryRecord[] = [];

type Params = {
  currentBranchId: string | null | undefined;
  selectedCategory: string | 'all' | null;
  setCategory: (category: string | 'all' | null) => void;
  categories: CategoryRecord[];
};

export function usePosCatalogFilters({
  currentBranchId,
  selectedCategory,
  setCategory,
  categories,
}: Params) {
  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounced = useDebouncedValue(searchQuery.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);
  const effectiveSearchQuery = useMemo(
    () => effectiveProductSearchQuery(searchDebounced),
    [searchDebounced]
  );
  const [itemTypeFilter, setItemTypeFilter] = useState<PosCatalogItemTypeFilter>('all');
  const [posPhysicalStockOnly, setPosPhysicalStockOnly] = useState(false);
  const [posBrandFilter, setPosBrandFilter] = useState<string | null>(null);
  const [posSupplierFilter, setPosSupplierFilter] = useState<string | null>(null);
  const [posBrandOptions, setPosBrandOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [posSupplierOptions, setPosSupplierOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [categorySelectorVisible, setCategorySelectorVisible] = useState(false);

  const reloadFilterOptions = useCallback(() => {
    try {
      void Promise.all([listDistinctProductBrandsLocal(), listActiveSuppliersLocal()]).then(
        ([brands, suppliers]) => {
          setPosBrandOptions(brands);
          setPosSupplierOptions(suppliers);
        }
      );
    } catch (e) {
      console.warn('reloadFilterOptions failed', e);
    }
  }, []);

  useEffect(() => {
    reloadFilterOptions();
  }, [reloadFilterOptions]);

  useEffect(() => {
    let alive = true;
    void AsyncStorage.getItem(POS_GRID_PHYSICAL_STOCK_ONLY_STORAGE_KEY).then((raw) => {
      if (!alive) return;
      if (raw === '1') {
        setPosPhysicalStockOnly(true);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  const togglePosPhysicalStockOnly = useCallback(() => {
    if (!inventoryStockUxEnabled) return;
    setPosPhysicalStockOnly((prev) => {
      const next = !prev;
      void AsyncStorage.setItem(POS_GRID_PHYSICAL_STOCK_ONLY_STORAGE_KEY, next ? '1' : '0');
      return next;
    });
  }, [inventoryStockUxEnabled]);

  useEffect(() => {
    if (!inventoryStockUxEnabled && posPhysicalStockOnly) {
      setPosPhysicalStockOnly(false);
      void AsyncStorage.setItem(POS_GRID_PHYSICAL_STOCK_ONLY_STORAGE_KEY, '0');
    }
  }, [inventoryStockUxEnabled, posPhysicalStockOnly]);

  const categoryOptions = useMemo(() => {
    // 'package' no es itemType de producto — sin filtro de categoría aplicable.
    const forItemType =
      itemTypeFilter === 'all' || itemTypeFilter === 'package' ? undefined : itemTypeFilter;
    return buildCategoryOptions(categories, {
      includeAll: true,
      allLabel: 'Todos',
      allColor: ticketColors.brandPrimary,
      fallbackCategories: EMPTY_CATEGORY_FALLBACK,
      forItemType,
    });
  }, [categories, itemTypeFilter]);

  const selectedCategoryRecord = useMemo(() => {
    if (selectedCategory === 'all') {
      return null;
    }
    const normalizedSelectedCategory = normalizeProductCategoryId(selectedCategory);
    return (
      categories.find((category) => normalizeProductCategoryId(category.id) === normalizedSelectedCategory) ??
      null
    );
  }, [categories, selectedCategory]);

  const handleSetItemTypeFilter = useCallback(
    (nextFilter: PosCatalogItemTypeFilter) => {
      setItemTypeFilter(nextFilter);
      const categoryStillMatches =
        nextFilter === 'all' ||
        nextFilter === 'package' ||
        selectedCategoryRecord == null ||
        categoryAppliesToDraftItemType(selectedCategoryRecord.appliesTo, nextFilter);
      if (!categoryStillMatches) {
        setCategory('all');
      }
    },
    [selectedCategoryRecord, setCategory]
  );

  const hasPosCatalogCriteria = useMemo(
    () =>
      searchQuery.trim().length > 0 ||
      selectedCategory !== 'all' ||
      itemTypeFilter !== 'all' ||
      (Boolean(currentBranchId) && posPhysicalStockOnly) ||
      posBrandFilter != null ||
      posSupplierFilter != null,
    [
      currentBranchId,
      itemTypeFilter,
      posBrandFilter,
      posPhysicalStockOnly,
      posSupplierFilter,
      searchQuery,
      selectedCategory,
    ]
  );

  useEffect(() => {
    if (selectedCategory === 'all') {
      return;
    }
    const normalizedSelectedCategory = normalizeProductCategoryId(selectedCategory);
    const isAvailable = categoryOptions.some(
      (category) => normalizeProductCategoryId(category.id) === normalizedSelectedCategory
    );
    if (!isAvailable) {
      setCategory('all');
    }
  }, [categoryOptions, selectedCategory, setCategory]);

  const handleClearCatalogFilters = useCallback(() => {
    setSearchQuery('');
    setItemTypeFilter('all');
    setCategory('all');
    setPosBrandFilter(null);
    setPosSupplierFilter(null);
    setPosPhysicalStockOnly(false);
    setCategorySelectorVisible(false);
    void AsyncStorage.setItem(POS_GRID_PHYSICAL_STOCK_ONLY_STORAGE_KEY, '0');
  }, [setCategory]);

  const handleSelectCategoryFromMore = useCallback(
    (categoryId: string) => {
      setCategory(categoryId as string | 'all');
      setSearchQuery('');
    },
    [setCategory]
  );

  return {
    searchQuery,
    setSearchQuery,
    effectiveSearchQuery,
    itemTypeFilter,
    handleSetItemTypeFilter,
    posPhysicalStockOnly: inventoryStockUxEnabled && posPhysicalStockOnly,
    togglePosPhysicalStockOnly,
    posBrandFilter,
    setPosBrandFilter,
    posSupplierFilter,
    setPosSupplierFilter,
    posBrandOptions,
    posSupplierOptions,
    reloadFilterOptions,
    hasPosCatalogCriteria,
    categoryOptions,
    handleClearCatalogFilters,
    categorySelectorVisible,
    setCategorySelectorVisible,
    handleSelectCategoryFromMore,
  };
}
